import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import useHostSummary from '../../hooks/useHostSummary';
import './topHeader.css';

/**
 * HOST in the desktop top bar — top-header handoff §3 (Sept 25 2026). Below lg it is the raised
 * centre of the bottom nav instead (MobileTabBar), which reads the same count.
 *
 *   A 40px circle: black inside, the RING flowing around its 2px border on a 3s loop. From 1600
 *   it widens to a pill with the HOST label. Routes to /host, where all creating starts.
 *
 * The badge is the host's running events — selling · funding · processing_greenlight ·
 * greenlit, the LIVE bucket on /host — hidden at 0, `9+` above 9. Reduced motion: the ring
 * holds still.
 */

/** Velvet-rope stanchions: the host holds the rope. At 16px it reads as an "H". */
export function HostIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="square" strokeLinejoin="miter" aria-hidden="true">
      <circle cx="5" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <circle cx="19" cy="5" r="1.6" fill="currentColor" stroke="none" />
      <path d="M5 7.5V20M19 7.5V20M2.5 20.5h5M16.5 20.5h5" />
      <path d="M5 9c3 5 11 5 14 0" stroke="#FF00AA" strokeWidth="2.4" />
    </svg>
  );
}

export const hostCountLabel = (n) => (n > 9 ? '9+' : String(n));
export const hostAriaLabel = (n) => `Host · ${n} ${n === 1 ? 'event' : 'events'} running`;

/** The live count, for the header circle and the bottom nav alike. */
export function useHostCount(countProp, { enabled = true } = {}) {
  const summary = useHostSummary({ enabled: enabled && countProp == null });
  return countProp ?? summary.liveEventCount ?? 0;
}

export default function HostEntry({
  label = false,
  /** Test seam; the bar reads the live count from useHostSummary. */
  count: countProp,
  className = '',
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const count = useHostCount(countProp);
  const active = pathname === '/host' || pathname.startsWith('/host/');

  return (
    <button
      type="button"
      onClick={() => navigate('/host')}
      aria-label={hostAriaLabel(count)}
      aria-current={active ? 'page' : undefined}
      title="Host"
      data-portal-to="/host"
      data-label={label ? 'true' : 'false'}
      className={`th-host th-host-ring ${className}`}
    >
      <HostIcon size={18} />
      {label && <span>HOST</span>}
      {count > 0 && <span className="th-badge" data-testid="host-count">{hostCountLabel(count)}</span>}
    </button>
  );
}
